// Catalog.ID Client — Agent Detail Pane
// ============================================================================
// Shows agent identity details with edit, API keys, tokens, delegation, terminate.

import { createEntityPaneType } from '@catalog-shell';
import type { EntityPaneConfig, ViewFieldDef, FormFieldDef, EntityAction } from '@catalog-shell';
import * as agentApi from '../api/agent';
import * as tokenApi from '../api/token';
import { formatApiError } from '../api/client';
import type { AgentDetail } from '../api/types';
import { identity } from '../state/identity';
import { createCredentialDisplay } from '../components/credential-display';
import type { CredentialField } from '../components/credential-display';
import { PANE_IDS } from '../shell-config';

const TYPE_LABELS: Record<string, string> = {
    ai_model: 'AI Model',
    automation: 'Automation',
    human_operator: 'Human Operator',
};

const viewFields: ViewFieldDef<AgentDetail>[] = [
    { key: 'cid', label: 'CID', type: 'text' },
    { key: 'agentLabel', label: 'Label', type: 'text' },
    {
        key: 'agentType', label: 'Type', type: 'badge',
        colorMap: { ai_model: 'rgba(176,128,224,0.3)', automation: 'rgba(176,128,224,0.3)', human_operator: 'rgba(176,128,224,0.3)' },
        labelMap: TYPE_LABELS,
    },
    { key: 'principalCid', label: 'Principal', type: 'text' },
    {
        key: 'status', label: 'Status', type: 'badge',
        colorMap: { active: 'rgba(80,200,80,0.3)', terminated: 'rgba(220,50,50,0.3)' },
        labelMap: { active: 'Active', terminated: 'Terminated' },
    },
    { key: 'creationTime', label: 'Created', type: 'date', format: 'datetime' },
];

const editFields: FormFieldDef<AgentDetail>[] = [
    { key: 'agentLabel', label: 'Label', type: 'text', required: true },
    {
        key: 'agentType', label: 'Type', type: 'select',
        options: [
            { key: 'ai_model', label: 'AI Model' },
            { key: 'automation', label: 'Automation' },
            { key: 'human_operator', label: 'Human Operator' },
        ],
    },
];

const actions: EntityAction<AgentDetail>[] = [
    {
        key: 'api-keys',
        label: 'API Keys',
        modes: ['view'],
        canExecute: (model) => model.status === 'active',
        disabledReason: () => 'Only active agents have API keys',
        execute: (_model, ctx) => {
            const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
            surface?.openPane({
                typeId: PANE_IDS.agentApiKeys,
                title: 'API Keys',
                context: { agentCid: ctx.instance.context['agentCid'] },
            }, { originPaneId: ctx.instance.instanceId });
        },
    },
    {
        key: 'tokens',
        label: 'Tokens',
        modes: ['view'],
        canExecute: (model) => model.status === 'active',
        disabledReason: () => 'Only active agents have refresh tokens',
        execute: (_model, ctx) => {
            const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
            surface?.openPane({
                typeId: PANE_IDS.agentTokens,
                title: 'Refresh Tokens',
                context: { agentCid: ctx.instance.context['agentCid'] },
            }, { originPaneId: ctx.instance.instanceId });
        },
    },
    {
        key: 'delegation-grant',
        label: 'Delegation Grant',
        modes: ['view'],
        overflow: true,
        canExecute: (model) => model.status === 'active',
        disabledReason: () => 'Only active agents can be issued a delegation grant',
        execute: async (model, ctx) => {
            const content = document.createElement('div');
            content.className = 'cid-wizard-step';

            content.appendChild(Object.assign(document.createElement('h3'), {
                className: 'cid-wizard-heading', textContent: 'Issue Delegation Grant',
            }));

            content.appendChild(Object.assign(document.createElement('p'), {
                className: 'cid-wizard-text',
                textContent: 'Select the scopes this delegation token may use. Only the selected operations will be permitted.',
            }));

            const canManageOrgKeys = identity.getActive()?.accountType === 'delegate'
                && (identity.hasRole('owner') || identity.hasRole('admin'));
            const availableScopes = agentApi.filterAgentScopes(canManageOrgKeys);

            const selectedScopes = new Set<string>();
            const scopeGroup = document.createElement('div');
            scopeGroup.className = 'cid-scope-list';
            for (const scope of availableScopes) {
                const row = document.createElement('label');
                row.className = 'cid-auth-checkbox-row';
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.id = `delegation-scope-${scope.id}`;
                cb.addEventListener('change', () => {
                    if (cb.checked) selectedScopes.add(scope.id);
                    else selectedScopes.delete(scope.id);
                });
                row.appendChild(cb);
                const text = document.createElement('div');
                text.className = 'cid-scope-text';
                const labelEl = document.createElement('div');
                labelEl.className = 'cid-scope-label';
                labelEl.textContent = scope.label;
                text.appendChild(labelEl);
                const descEl = document.createElement('div');
                descEl.className = 'cid-scope-description';
                descEl.textContent = scope.description;
                text.appendChild(descEl);
                row.appendChild(text);
                scopeGroup.appendChild(row);
            }
            content.appendChild(scopeGroup);

            const submitBtn = document.createElement('button');
            submitBtn.className = 'cs-btn cs-btn--yes';
            submitBtn.textContent = 'Generate Token';
            content.appendChild(submitBtn);

            const modal = ctx.shell.showModal(content);

            submitBtn.addEventListener('click', async () => {
                if (selectedScopes.size === 0) { ctx.shell.toast('Select at least one scope', 'warning'); return; }

                submitBtn.setAttribute('disabled', '');
                submitBtn.textContent = 'Generating...';

                try {
                    const resp = await tokenApi.agentDelegationGrant(model.cid, Array.from(selectedScopes));

                    content.innerHTML = '';
                    content.appendChild(Object.assign(document.createElement('h3'), {
                        className: 'cid-wizard-heading', textContent: 'Delegation Token',
                    }));
                    content.appendChild(Object.assign(document.createElement('p'), {
                        className: 'cid-wizard-warning',
                        textContent: 'Save these tokens now. They will not be shown again.',
                    }));

                    const fields: CredentialField[] = [
                        { label: 'Agent CID', value: model.cid },
                        { label: 'Scopes', value: Array.from(selectedScopes).join(', '), monospace: false },
                        { label: 'Access Token', value: resp.accessToken },
                        { label: 'Expires In', value: `${resp.expiresIn} seconds`, monospace: false },
                    ];
                    if (resp.refreshToken) {
                        fields.push({ label: 'Refresh Token', value: resp.refreshToken });
                    }
                    const credDisplay = createCredentialDisplay({
                        fields,
                        downloadFilename: `catalog-agent-token-${model.cid}.txt`,
                        downloadFooter: 'Keep this file in a secure location. These tokens grant access on behalf of the agent. If compromised, revoke the refresh token from the Tokens pane or terminate the agent.',
                    });
                    content.appendChild(credDisplay);

                    const doneBtn = document.createElement('button');
                    doneBtn.className = 'cs-btn cs-btn--yes';
                    doneBtn.textContent = 'Done';
                    doneBtn.addEventListener('click', () => modal.close());
                    content.appendChild(doneBtn);
                } catch (err) {
                    submitBtn.removeAttribute('disabled');
                    submitBtn.textContent = 'Generate Token';
                    ctx.shell.toast(formatApiError(err, 'Failed to generate token'), 'error');
                }
            });
        },
    },
    {
        key: 'terminate',
        label: 'Terminate',
        modes: ['view'],
        overflow: true,
        variant: 'danger',
        canExecute: (model) => model.status === 'active',
        disabledReason: () => 'This agent is already terminated',
        execute: async (model, ctx) => {
            const ok = await ctx.shell.confirm({
                title: 'Terminate Agent',
                message: `Permanently terminate agent ${model.cid} (${model.agentLabel})? This cannot be undone.`,
                confirmLabel: 'Terminate',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                await agentApi.terminateAgent(model.cid);
                ctx.shell.toast('Agent terminated', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(formatApiError(err, 'Termination failed'), 'error');
            }
        },
    },
];

const config: EntityPaneConfig<AgentDetail> = {
    typeId: PANE_IDS.agentDetail,
    defaultTitle: 'Agent',
    ariaLabel: 'Agent details',
    refreshOnDataChanged: 'agents',
    // Verbs live in the pane's own title row (shell USAGE.md "Where a button
    // belongs"): API Keys / Tokens inline, the rarer Delegation Grant and the
    // destructive Terminate under the trailing ⋯ menu.
    actionsPlacement: 'pane',

    getModel: async (ctx) => {
        const cid = ctx.instance.context['agentCid'] as string;
        return agentApi.getAgent(cid);
    },

    view: {
        fields: viewFields,
        layout: [
            ['cid', 'status'],
            ['agentLabel', 'agentType'],
            ['principalCid', 'creationTime'],
        ],
    },

    form: {
        fields: editFields,
        layout: [['agentLabel'], ['agentType']],
    },

    canEdit: (model) => model.status === 'active',
    canDelete: false,

    onUpdate: async (model, ctx) => {
        await agentApi.updateAgent(model.cid, {
            label: model.agentLabel,
            agentType: model.agentType,
        });
        ctx.shell.toast('Agent updated', 'success');
    },

    actions,
};

export function createAgentDetailPane() {
    return createEntityPaneType(config);
}
